import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, Trophy, Play, Brain } from "lucide-react";
import { useStore } from "../context/StoreContext";
import Coin from "../components/Coin";
import { BRAIN_GAMES, ARCADE_GAMES } from "../data/games";
import sfx from "../utils/sound";
import usePageMeta from "../hooks/usePageMeta";
import AdBanner from "../components/AdBanner";

const GameTile = ({ g }) => (
  <Link
    key={g.id}
    to={`/games/${g.id}`}
    className="chunky-card pushable overflow-hidden flex flex-col"
    data-testid={`game-tile-${g.id}`}
    onClick={() => sfx.click()}
  >
    <div
      className="relative flex items-center justify-center"
      style={{
        background: g.color,
        height: 120,
        borderBottom: "3px solid #2b2b2b",
      }}
    >
      <span
        className="text-6xl bob"
        style={{ filter: "drop-shadow(0 3px 0 rgba(0,0,0,0.25))" }}
        aria-hidden="true"
      >
        {g.emoji}
      </span>
    </div>
    <div className="p-3 sm:p-4 flex flex-col gap-2 flex-1">
      <div className="font-display text-base sm:text-lg leading-tight">{g.name}</div>
      <div className="text-xs sm:text-sm" style={{ color: "#484848" }}>
        {g.tagline}
      </div>
      <div className="mt-auto pt-2 flex items-center justify-between">
        <div className="coin-pill text-xs">
          <Coin size={14} />
          {g.reward}
        </div>
        <div
          className="rounded-full flex items-center justify-center"
          style={{
            width: 36,
            height: 36,
            background: "#00A699",
            border: "3px solid #2b2b2b",
            boxShadow: "0 3px 0 #2b2b2b",
            color: "white",
          }}
        >
          <Play size={16} strokeWidth={3} fill="white" />
        </div>
      </div>
    </div>
  </Link>
);

const Games = () => {
  const { gameStats } = useStore();
  usePageMeta(
    "Game Zone · 67Cafe",
    "Play arcade mini-games plus math quizzes, word problems, trivia and classic board games at 67Cafe — earn coins for the pretend store."
  );

  return (
    <div
      className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8"
      data-testid="games-page"
    >
      <Link
        to="/shop"
        className="btn-3d btn-sm btn-ghost mb-4"
        data-testid="games-back-btn"
      >
        <ArrowLeft size={18} strokeWidth={3} /> Back
      </Link>

      {/* Header + stats */}
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl leading-tight">
            <span className="wiggle inline-block mr-2">🎮</span>
            Game Zone
          </h1>
          <p className="text-base sm:text-lg" style={{ color: "#484848" }}>
            Play, learn, and stack coins for the store!
          </p>
        </div>
        <div
          className="chunky-card px-4 py-3 flex items-center gap-4"
          style={{ boxShadow: "0 5px 0 #2b2b2b" }}
          data-testid="game-stats"
        >
          <div className="flex items-center gap-2">
            <Trophy size={18} strokeWidth={3} style={{ color: "#FFB400" }} />
            <span className="font-display text-sm">Stats</span>
          </div>
          <div className="text-xs" style={{ color: "#484848" }}>
            Spins: <strong>{gameStats.wheelSpins}</strong> · Wins:{" "}
            <strong>{gameStats.cupsWon}</strong>
          </div>
          <div
            className="inline-flex items-center gap-1 text-sm font-display"
            style={{ color: "#00A699" }}
          >
            <Coin size={16} />+{gameStats.coinsEarned}
          </div>
        </div>
      </div>

      {/* Brain / board games */}
      <section className="mb-10" data-testid="brain-library-section">
        <h2 className="font-display text-xl sm:text-2xl mb-3 flex items-center gap-2">
          <Brain size={20} strokeWidth={3} style={{ color: "#5F27CD" }} />
          Brain Boosters &amp; Board Games
        </h2>
        <div
          className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6"
          data-testid="brain-grid"
        >
          {BRAIN_GAMES.map((g) => (
            <GameTile key={g.id} g={g} />
          ))}
        </div>
      </section>

      {/* Sponsored — self-hosted between-rows ad slot. */}
      <div className="my-6" data-testid="games-ad-slot">
        <AdBanner slot="games_row" />
      </div>

      {/* Arcade games */}
      <section data-testid="games-library-section">
        <h2 className="font-display text-xl sm:text-2xl mb-3 flex items-center gap-2">
          <Play size={20} strokeWidth={3} style={{ color: "#5F27CD" }} fill="#5F27CD" />
          Arcade Library
        </h2>
        <div
          className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6"
          data-testid="games-grid"
        >
          {ARCADE_GAMES.map((g) => (
            <GameTile key={g.id} g={g} />
          ))}
        </div>
      </section>

      {/* Sponsored — bottom-of-grid slot. */}
      <div className="mt-6" data-testid="games-ad-slot-bottom">
        <AdBanner slot="games_row_bottom" />
      </div>

      <div
        className="chunky-card p-4 mt-8 flex items-center gap-3"
        style={{ background: "#FFF9E6" }}
      >
        <Sparkles size={20} strokeWidth={3} style={{ color: "#FFB400" }} />
        <p className="text-sm sm:text-base" style={{ color: "#484848" }}>
          Every coin you earn goes straight into your wallet. Shop, play,
          repeat!
        </p>
      </div>
    </div>
  );
};

export default Games;
